/**
 * MarkdownLite — a tiny, dependency-free markdown renderer for agent replies.
 * Handles the subset Megan produces: tables, **bold**, bullet lists, and
 * paragraphs/line breaks. Falls back to plain text on anything unexpected.
 */
import React from 'react';

// Render inline **bold** within a string.
function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return (
        <strong key={`${keyPrefix}-b${i}`} className="font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-t${i}`}>{p}</React.Fragment>;
  });
}

const isSeparatorRow = (cells) =>
  cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c.trim()));

const splitRow = (line) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

function renderTable(tableLines, id) {
  const rows = tableLines.map(splitRow).filter((r) => !isSeparatorRow(r));
  if (!rows.length) return null;
  const [header, ...body] = rows;
  return (
    <div key={`tbl-${id}`} className="overflow-x-auto my-2 rounded-lg border border-grey-200 dark:border-grey-700">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-grey-50 dark:bg-grey-800/60 text-left">
            {header.map((h, hi) => (
              <th key={hi} className="px-2.5 py-1.5 font-semibold text-grey-700 dark:text-grey-200">
                {renderInline(h, `th-${id}-${hi}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri} className="border-t border-grey-200 dark:border-grey-800">
              {r.map((c, ci) => (
                <td key={ci} className="px-2.5 py-1.5 text-grey-700 dark:text-grey-300">
                  {renderInline(c, `td-${id}-${ri}-${ci}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderList(items, id) {
  return (
    <ul key={`ul-${id}`} className="list-disc pl-5 my-1.5 space-y-0.5">
      {items.map((it, ii) => (
        <li key={ii}>{renderInline(it, `li-${id}-${ii}`)}</li>
      ))}
    </ul>
  );
}

function renderParagraph(para, id) {
  return (
    <p key={`p-${id}`} className="my-1 leading-relaxed whitespace-pre-wrap">
      {para.map((pl, pi) => (
        <React.Fragment key={pi}>
          {pi > 0 && <br />}
          {renderInline(pl, `p-${id}-${pi}`)}
        </React.Fragment>
      ))}
    </p>
  );
}

export function MarkdownLite({ text }) {
  if (!text) return null;
  const lines = String(text).split('\n');
  const blocks = [];
  let i = 0;
  let id = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim().startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const node = renderTable(tableLines, id++);
      if (node) blocks.push(node);
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(renderList(items, id++));
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().startsWith('|') &&
      !/^\s*[-*]\s+/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(renderParagraph(para, id++));
  }

  return <div className="space-y-0.5">{blocks}</div>;
}

export default MarkdownLite;
